import { Cancel, Check, Edit, ExitToApp, Remove } from '@mui/icons-material';
import { Button } from '@mui/material';
import { RolesStructureEnum } from 'domain/security/RolesStructureEnum';
import { StructureMember } from 'domain/structureMember/StructureMember';
import { StructureMemberState } from 'domain/structureMember/StructureMemberState';
import { insertIf } from 'helper/array.helper';
import {
  PopperButton,
  PopperButtonsConf,
} from 'primary/Components/PopperButton/PopperButton';
import { sendEventToastMessage } from 'primary/Components/Toast/Toast.helper';
import { routesConfig } from 'primary/Configs/Routes.config';
import { useContextDependency } from 'primary/hooks/useContextDependency';
import { useTranslate } from 'primary/hooks/useTranslate';
import React from 'react';
import { useNavigate } from 'react-router-dom';

type StructureMembersDatatableMenuProps = {
  member: StructureMember;
  readonly?: boolean;
  onClickEditMember?: (member: StructureMember) => void;
  onClickDeleteMember?: (member: StructureMember) => void;
  retry: () => void;
};

export const StructureMembersDatatableMenu = ({
  member,
  readonly,
  onClickEditMember,
  onClickDeleteMember,
  retry,
}: StructureMembersDatatableMenuProps) => {
  const t = useTranslate();
  const navigate = useNavigate();
  const { authRepository, structureMemberRepository } = useContextDependency();
  const isAcceptedMember = member.state === StructureMemberState.ACCEPTED;
  const onClickResponse = (response: boolean) => {
    if (!member.structure) return;
    structureMemberRepository
      .responseMemberInvitation(response, member?.user?.id, member.structure.id)
      .then(() => {
        retry();
        sendEventToastMessage(
          !response
            ? t('notification.content.invitationStructure.response.refused')
            : t('notification.content.invitationStructure.response.accepted'),
          'success',
        );
      });
  };
  const isWaitingForAccept = [
    StructureMemberState.WAITING_JOIN_ACCEPTED,
    StructureMemberState.WAITINT_LINK_ACCEPTED,
  ].includes(member.state);
  const isWaitingInviteAccept =
    member.state === StructureMemberState.WAITING_INVITE_ACCEPTED;
  const canDeleteMember =
    member?.user?.id != authRepository.currentUser?.id &&
    member.role.code !== RolesStructureEnum.PRES;
  const conf: PopperButtonsConf[] = [
    ...insertIf(isAcceptedMember, {
      label: t('structure.members.datatable.seeProfil'),
      action: () => {
        navigate(routesConfig.userProfil.to(member?.user?.userProfil?.id));
      },
      icon: <ExitToApp />,
    }),
    ...insertIf(isAcceptedMember && !readonly, {
      label: t('structure.members.datatable.editRole'),
      action: () => {
        onClickEditMember && onClickEditMember(member as StructureMember);
      },
      icon: <Edit />,
    }),
    ...insertIf(!isWaitingForAccept && !isWaitingInviteAccept && canDeleteMember, {
      label: t('structure.members.datatable.deleteMember'),
      action: () => {
        onClickDeleteMember && onClickDeleteMember(member as StructureMember);
      },
      icon: <Remove />,
    }),
    ...insertIf(isWaitingForAccept, {
      button: (
        <>
          <Button
            color={'secondary'}
            variant={'contained'}
            size={'small'}
            onClick={() => onClickResponse(true)}
          >
            <Check />
            {t('structure.members.datatable.accept')}
          </Button>
          <Button
            color={'secondary'}
            variant={'contained'}
            size={'small'}
            onClick={() => onClickResponse(false)}
          >
            <Cancel />
            <span>{t('structure.members.datatable.decline')}</span>
          </Button>
        </>
      ),
    }),
    ...insertIf(isWaitingInviteAccept, {
      button: (
        <>
          <Button
            size={'small'}
            color={'secondary'}
            variant={'contained'}
            onClick={() => {
              onClickDeleteMember && onClickDeleteMember(member as StructureMember);
            }}
          >
            <Remove />
            {t('structure.members.datatable.cancelInvitation')}
          </Button>
        </>
      ),
    }),
  ];

  return (
    <PopperButton
      key={'user-profil-preview'}
      conf={conf}
      popperName={'user-profil-preview'}
      placement={'auto'}
    />
  );
};
